// src/pages/public/PublicSeoPage.tsx
import { createElement, useMemo } from 'react';
import type { ReactElement, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import SeoHead from '../../seo/SeoHead';
import { LinkRendererContext, PublicPageContent } from '../../seo/core/publicPageMarkup';
import MainNavigation from '../../components/common/MainNavigation';
import SiteFooter from '../../components/common/SiteFooter';
import type { PublicPageModel } from '../../seo/types';
import '../../styles/seo-public.css';

export interface PublicSeoPageProps {
  model: PublicPageModel | null;
  loading?: boolean;
}

/**
 * The single shell every public SEO page renders through.
 *
 * The page body itself comes from `seo/core/publicPageMarkup.js` — the very
 * same component tree the build-time prerenderer runs through
 * `renderToStaticMarkup`. That is what guarantees a crawler with JavaScript
 * disabled and a human with the SPA booted see identical content: there is one
 * component tree, not a crawler copy and a user copy.
 *
 * The only difference between the two renders is link behaviour: here we inject
 * react-router `Link` so navigation stays client-side, while the prerenderer
 * emits plain crawlable `<a href>` elements.
 */
function PublicSeoPage({ model, loading }: PublicSeoPageProps) {
  const renderLink = useMemo(
    () =>
      function renderRouterLink(props: {
        to: string;
        children: ReactNode;
        className?: string;
        rel?: string;
      }): ReactElement {
        return createElement(
          Link,
          { to: props.to, className: props.className, rel: props.rel },
          props.children
        );
      },
    []
  );

  return (
    <>
      <MainNavigation variant="solid" />
      {model ? <SeoHead metadata={model.metadata} jsonLd={model.jsonLd} /> : null}
      <div className="seo-public-shell">
        {model ? (
          <LinkRendererContext.Provider value={renderLink}>
            <PublicPageContent model={model} />
          </LinkRendererContext.Provider>
        ) : (
          <p className="seo-public-shell__loading">
            {loading ? 'Loading…' : 'This page is not available right now.'}
          </p>
        )}
      </div>
      <SiteFooter />
    </>
  );
}

export default PublicSeoPage;
